import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { useAuthStore } from '../../context/auth'
import { Badge, Button, Card, ErrorText, Field, Input, Modal, Spinner, Table, Textarea, EmptyState } from '../../components/ui'
import { formatDateTime } from '../../utils/format'

interface Post {
  id: string
  slug: string
  title: string
  excerpt?: string | null
  body: string
  tags: string[]
  status: 'DRAFT' | 'PENDING_REVIEW' | 'PUBLISHED'
  publishedAt?: string | null
  updatedAt: string
  author: { firstName: string; lastName: string }
}

const STATUS: Record<string, { label: string; className: string }> = {
  DRAFT: { label: 'Borrador', className: 'bg-gray-100 text-gray-700' },
  PENDING_REVIEW: { label: 'En revisión', className: 'bg-amber-100 text-amber-800' },
  PUBLISHED: { label: 'Publicado', className: 'bg-emerald-100 text-emerald-800' },
}

const EMPTY_FORM = { title: '', excerpt: '', body: '', tags: '' }

export function BlogAdminPage() {
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const isAdmin = user?.role === 'ADMIN'
  const [error, setError] = useState('')
  const [editing, setEditing] = useState<Post | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)

  const posts = useQuery({
    queryKey: ['blog-admin'],
    queryFn: async () => (await api.get<Post[]>('/blog')).data,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['blog-admin'] })
    queryClient.invalidateQueries({ queryKey: ['public-blog'] })
  }

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        title: form.title,
        excerpt: form.excerpt || null,
        body: form.body,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      }
      if (editing) return (await api.patch(`/blog/${editing.id}`, payload)).data
      return (await api.post('/blog', payload)).data
    },
    onSuccess: () => {
      setShowForm(false)
      setEditing(null)
      setForm(EMPTY_FORM)
      setError('')
      invalidate()
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const setStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => (await api.post(`/blog/${id}/status`, { status })).data,
    onSuccess: invalidate,
    onError: (err) => setError(apiErrorMessage(err)),
  })

  function openEdit(post: Post) {
    setEditing(post)
    setForm({ title: post.title, excerpt: post.excerpt ?? '', body: post.body, tags: post.tags.join(', ') })
    setShowForm(true)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-bold text-ink">Blog</h1>
        <Button variant="cta" onClick={() => { setEditing(null); setForm(EMPTY_FORM); setShowForm(true) }}>
          + Nuevo artículo
        </Button>
      </div>

      <ErrorText>{error}</ErrorText>

      <Card>
        {posts.isLoading ? (
          <Spinner />
        ) : (posts.data?.length ?? 0) === 0 ? (
          <EmptyState message="Sin artículos. Los médicos redactan borradores y Administración los publica." />
        ) : (
          <Table headers={['Título', 'Autor', 'Actualizado', 'Estado', '']}>
            {(posts.data ?? []).map((p) => (
              <tr key={p.id}>
                <td className="px-2 py-2">
                  <span className="font-medium text-ink">{p.title}</span>
                  <span className="block font-mono text-xs text-gray-400">/blog/{p.slug}</span>
                </td>
                <td className="px-2 py-2">{p.author.firstName} {p.author.lastName}</td>
                <td className="px-2 py-2 text-xs text-gray-500">{formatDateTime(p.updatedAt)}</td>
                <td className="px-2 py-2"><Badge className={STATUS[p.status].className}>{STATUS[p.status].label}</Badge></td>
                <td className="px-2 py-2">
                  <div className="flex flex-wrap gap-1.5">
                    <Button variant="ghost" onClick={() => openEdit(p)}>Editar</Button>
                    {p.status === 'DRAFT' && (
                      <Button variant="secondary" onClick={() => setStatus.mutate({ id: p.id, status: 'PENDING_REVIEW' })}>
                        Enviar a revisión
                      </Button>
                    )}
                    {isAdmin && p.status !== 'PUBLISHED' && (
                      <Button variant="cta" onClick={() => setStatus.mutate({ id: p.id, status: 'PUBLISHED' })}>
                        Publicar
                      </Button>
                    )}
                    {isAdmin && p.status === 'PUBLISHED' && (
                      <Button variant="secondary" onClick={() => setStatus.mutate({ id: p.id, status: 'DRAFT' })}>
                        Despublicar
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {showForm && (
        <Modal title={editing ? 'Editar artículo' : 'Nuevo artículo'} onClose={() => setShowForm(false)} wide>
          <div className="space-y-3">
            <Field label="Título">
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </Field>
            <Field label="Bajada (extracto)">
              <Input value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} maxLength={300} />
            </Field>
            <Field label="Contenido" hint="Separa párrafos con una línea en blanco.">
              <Textarea rows={10} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
            </Field>
            <Field label="Etiquetas" hint="Separadas por coma: prevención, rodilla, fútbol">
              <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
            </Field>
            {editing?.status === 'PUBLISHED' && !isAdmin && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Al editar un artículo publicado volverá a revisión hasta que Administración lo apruebe.
              </p>
            )}
            <ErrorText>{error}</ErrorText>
            <Button variant="cta" className="w-full" disabled={form.title.length < 3 || form.body.length < 10 || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'Guardando…' : 'Guardar'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
